import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MapPin, Loader2, ChevronDown, ChevronUp } from 'lucide-react';
import { HorizontalFilterBar } from '../../Listings/Filters/HorizontalFilterBar';
import { AddressSearch } from '../../Filters/AddressSearch';
import { categories } from '../../../lib/constants/categories';
import { europeanCountries } from '../../../lib/constants/countries';
import { languages } from '../../../lib/constants/languages';
import { workArrangements } from '../../../lib/constants';

interface Filters {
  categories: string[];
  countries: string[];
  languages: string[];
  workArrangements: string[];
  corporateWellness: boolean;
  nearMe: boolean;
}

interface HorizontalFiltersProps {
  filters: Filters;
  onFilterChange: (filterType: keyof Filters, value: string[] | boolean) => void;
  locationLoading?: boolean;
  onAddressSelect?: (latitude: number, longitude: number, address: string) => void;
  showExpanded?: boolean;
}

export function HorizontalFilters({ filters, onFilterChange, locationLoading, onAddressSelect, showExpanded = true }: HorizontalFiltersProps) {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(showExpanded);
  const hasActiveFilters = filters.categories.length > 0 ||
                          filters.countries.length > 0 ||
                          filters.languages.length > 0 ||
                          filters.workArrangements.length > 0 ||
                          filters.corporateWellness ||
                          filters.nearMe;

  const clearAllFilters = () => {
    onFilterChange('categories', []);
    onFilterChange('countries', []);
    onFilterChange('languages', []);
    onFilterChange('workArrangements', []);
    onFilterChange('corporateWellness', false);
    onFilterChange('nearMe', false);
  };

  const filterGroups = [
    {
      label: t('filters.categories'),
      options: categories,
      selectedValues: filters.categories,
      onChange: (values: string[]) => onFilterChange('categories', values),
      multiSelect: true
    },
    {
      label: t('filters.countries'),
      options: europeanCountries,
      selectedValues: filters.countries,
      onChange: (values: string[]) => onFilterChange('countries', values),
      multiSelect: true
    },
    {
      label: t('filters.languages'),
      options: languages,
      selectedValues: filters.languages,
      onChange: (values: string[]) => onFilterChange('languages', values),
      multiSelect: true
    },
    {
      label: t('filters.workType'),
      options: workArrangements,
      selectedValues: filters.workArrangements,
      onChange: (values: string[]) => onFilterChange('workArrangements', values),
      multiSelect: true
    },
    {
      label: t('practitioners.corporateWellness'),
      options: [{ value: 'corporate', label: t('practitioners.availableForCompanies') }],
      selectedValues: filters.corporateWellness ? ['corporate'] : [],
      onChange: (values: string[]) => onFilterChange('corporateWellness', values.length > 0),
      multiSelect: false
    }
  ];

  return (
    <div className="border-b border-gray-200 w-full">
      <div className="px-3 sm:px-4 lg:px-6 max-w-page mx-auto">
        {/* Mobile: compact row with Near Me + Filters button */}
        <div className="lg:hidden flex items-center gap-2 py-2">
          <button
            onClick={() => onFilterChange('nearMe', !filters.nearMe)}
            disabled={locationLoading}
            className={`flex-shrink-0 h-9 px-4 rounded-full gap-2 inline-flex items-center text-sm font-medium transition-colors active:scale-95 ${
              filters.nearMe
                ? 'bg-primary text-primary-foreground hover:bg-primary/90'
                : 'border border-input bg-background hover:bg-accent hover:text-accent-foreground'
            } ${locationLoading ? 'opacity-50 cursor-wait' : ''}`}
          >
            {locationLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <MapPin className="h-4 w-4" />}
            <span>{t('filters.nearMe')}</span>
          </button>
          <HorizontalFilterBar
            filterGroups={filterGroups}
            onClearAll={clearAllFilters}
            hasActiveFilters={hasActiveFilters}
            inline={true}
          />
        </div>

        {/* Desktop: full horizontal row */}
        <div className="hidden lg:block">
          <div className="w-full overflow-x-auto scrollbar-hide">
            <div className="flex items-center gap-1.5 sm:gap-2 py-2 min-w-max">
              <button
                onClick={() => onFilterChange('nearMe', !filters.nearMe)}
                disabled={locationLoading}
                className={`flex-shrink-0 h-9 px-4 rounded-full gap-2 inline-flex items-center text-sm font-medium transition-colors ${
                  filters.nearMe
                    ? 'bg-primary text-primary-foreground hover:bg-primary/90'
                    : 'border border-input bg-background hover:bg-accent hover:text-accent-foreground'
                } ${locationLoading ? 'opacity-50 cursor-wait' : ''}`}
              >
                {locationLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <MapPin className="h-4 w-4" />}
                <span>{t('filters.nearMe')}</span>
              </button>

              <HorizontalFilterBar
                filterGroups={filterGroups}
                onClearAll={clearAllFilters}
                hasActiveFilters={hasActiveFilters}
                inline={true}
              />

              <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="flex-shrink-0 h-9 px-3 rounded-full gap-2 inline-flex items-center text-sm font-medium border border-input bg-background hover:bg-accent hover:text-accent-foreground"
              >
                {isExpanded ? (
                  <>
                    <ChevronUp className="h-4 w-4" />
                    <span className="hidden sm:inline">{t('practitioners.less')}</span>
                  </>
                ) : (
                  <>
                    <ChevronDown className="h-4 w-4" />
                    <span className="hidden sm:inline">{t('practitioners.more')}</span>
                  </>
                )}
              </button>
            </div>
          </div>

          {isExpanded && onAddressSelect && (
            <div className="pt-4 pb-4 border-t border-gray-200 mt-2">
              <div className="max-w-md">
                <AddressSearch onAddressSelect={onAddressSelect} />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
